import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { EASING } from "../../utils/easingUtils";
import traiteurImg from "../../images/21.png";
import djImg from "../../images/22.png";
import decorationImg from "../../images/23.png";
import animationImg from "../../images/24.png";
import locationImg from "../../images/25.png";
import photographyImg from "../../images/26.png";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { FaStar, FaUsers, FaCrown, FaRocket, FaCheck } from "react-icons/fa";

// Default card data
const defaultCardData = [
  {
    id: 1,
    title: "Traiteurs",
    image: traiteurImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 3, md: 1 },
  },
  {
    id: 2,
    title: "Décoration",
    image: decorationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 3, md: 1 },
  },
  {
    id: 3,
    title: "Photographie et tournage",
    image: photographyImg,
    to: "/prestations",
    height: "516px",
    colSpan: { lg: 4, md: 1 },
  },
  {
    id: 4,
    title: "Musique et DJ",
    image: djImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 5, md: 2 },
  },
  {
    id: 5,
    title: "Animation",
    image: animationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 5, md: 1 },
  },
  {
    id: 6,
    title: "Location de matériel",
    image: locationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 7, md: 1 },
  },
];

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASING.easeOut },
  },
  hover: {
    scale: 1.05,
    y: -8,
    transition: { duration: 0.3, ease: EASING.easeOut },
  },
};

// Container animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// Title animation variants
const titleVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASING.easeOut },
  },
};

// Define prop types
interface CardData {
  id: number;
  title: string;
  image: string;
  to: string;
  height: string;
  colSpan: { lg: number; md: number };
}

interface CallToActionImageProps {
  title?: string;
  cardData?: CardData[];
  className?: string;
  controls?: any;
}

// Reusable CallToActionImage Component
function PrestaCallToActionImage({
  title = "Les prestataires événementiels autour de Paris",
  cardData = defaultCardData,
  className = "relative overflow-hidden",
  controls,
}: CallToActionImageProps) {
  return (
    <div
      className={`${className} bg-gradient-to-br dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900`}
    >
      {/* Background Effects */}
      <BgGlassmorphism />
      <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-primary-200/20 to-secondary-200/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-accent-200/15 to-primary-200/15 rounded-full blur-3xl"></div>

      <div className="container relative px-4 py-16 mx-auto">
        {/* Hero Section */}
        <motion.div className="text-center mb-16" variants={titleVariants} initial="hidden" animate="visible">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full text-sm font-medium text-primary-600 mb-6">
            <FaStar className="w-4 h-4 mr-2 text-primary-500" />
            Prestataires vérifiés
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
            {title}
          </h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
            Découvrez notre sélection de professionnels qualifiés pour faire de votre événement un succès
          </p>

          {/* Statistics */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaUsers className="w-5 h-5 text-primary-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">500+ prestataires</span>
            </div>
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaCrown className="w-5 h-5 text-accent-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">Qualité premium</span>
            </div>
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaStar className="w-5 h-5 text-yellow-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">4.9/5 étoiles</span>
            </div>
          </div>
        </motion.div>

        {/* Grid Layout */}
        <motion.div
          className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* First Column */}
          <div className="space-y-8 lg:col-span-3 md:col-span-1">
            {cardData.slice(0, 2).map((card, index) => (
              <Card key={card.id} card={card} index={index} />
            ))}
          </div>

          {/* Second Column */}
          <div className="lg:col-span-4 md:col-span-1">
            <Card card={cardData[2]} index={2} />
          </div>

          {/* Third Column */}
          <div className="lg:col-span-5 md:col-span-2">
            <Card card={cardData[3]} index={3} className="mb-8" />
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12">
              <div className="lg:col-span-5 md:col-span-1">
                <Card card={cardData[4]} index={4} />
              </div>
              <div className="lg:col-span-7 md:col-span-1">
                <Card card={cardData[5]} index={5} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action - Version interactive */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div className="relative overflow-hidden bg-white dark:bg-neutral-800 rounded-3xl border border-neutral-200 dark:border-neutral-700 shadow-xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-40 h-40 bg-primary-500 rounded-full -translate-x-20 -translate-y-20"></div>
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-secondary-500 rounded-full translate-x-16 translate-y-16"></div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 p-8">
              {/* Left Side - Content */}
              <div className="flex flex-col justify-center">
                <div className="inline-flex items-center px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm font-medium mb-4 w-fit">
                  <FaRocket className="w-4 h-4 mr-2" />
                  Rejoignez-nous
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                  Développez votre business
                </h3>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-6 leading-relaxed">
                  Accédez à des milliers de clients potentiels et boostez votre visibilité sur la plateforme n°1
                  des événements.
                </p>

                {/* Features list */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                      <FaCheck className="w-3 h-3 text-pink-600" />
                    </div>
                    <span className="text-neutral-700 dark:text-neutral-300">Inscription gratuite</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                      <FaCheck className="w-3 h-3 text-pink-600" />
                    </div>
                    <span className="text-neutral-700 dark:text-neutral-300">Commission réduite</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                      <FaCheck className="w-3 h-3 text-pink-600" />
                    </div>
                    <span className="text-neutral-700 dark:text-neutral-300">Support dédié</span>
                  </div>
                </div>

                <Link to="/signup/service">
                  <button className="group bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-xl w-fit">
                    <span className="flex items-center">
                      Créer mon profil
                      <svg
                        className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        ></path>
                      </svg>
                    </span>
                  </button>
                </Link>
              </div>

              {/* Right Side - Visual/Stats */}
              <div className="flex flex-col justify-center items-center">
                <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-primary-600 mb-1">500+</div>
                    <div className="text-sm text-neutral-600">Prestataires</div>
                  </div>
                  <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-secondary-600 mb-1">1000+</div>
                    <div className="text-sm text-neutral-600">Événements</div>
                  </div>
                  <div className="bg-gradient-to-br from-accent-50 to-accent-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-accent-600 mb-1">98%</div>
                    <div className="text-sm text-neutral-600">Satisfaction</div>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-pink-600 mb-1">24h</div>
                    <div className="text-sm text-neutral-600">Activation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* RTL Support */}
        <style>{`
          [dir='rtl'] .discount-single-item .discount {
            left: auto;
            right: 1.25rem;
          }
        `}</style>
      </div>
    </div>
  );
}

// Reusable Card Component
function Card({ card, index, className = "" }: { card: CardData; index: number; className?: string }) {
  return (
    <AnimatePresence>
      <motion.div
        className={`group relative block rounded-3xl overflow-hidden ${className} shadow-lg hover:shadow-2xl transition-shadow duration-500`}
        style={{ height: card.height }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        transition={{ delay: index * 0.1 }}
      >
        <Link to={card.to} className="block w-full h-full">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-center bg-cover transform group-hover:scale-110 transition-transform duration-700"
            style={{ backgroundImage: `url(${card.image})` }}
          />

          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-white/95 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-secondary-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Decorative Elements */}
          <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
            <FaStar className="w-5 h-5 text-white" />
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
            >
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-primary-500/90 backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-lg">
                  Premium
                </span>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 md:text-xl group-hover:text-primary-600 transition-colors duration-300 drop-shadow-lg">
                {card.title}
              </h3>
              <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm text-neutral-800 font-medium drop-shadow-md">
                  Découvrir les professionnels →
                </span>
              </div>
            </motion.div>
          </div>

          {/* Border Effect */}
          <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary-500/50 transition-colors duration-500" />
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}

export default PrestaCallToActionImage;
